import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Link,
  VStack,
  useToast,
  useTheme,
  useColorMode,
} from "@chakra-ui/react";
import moment from "moment";
import InputArea from "../ChatComponent/InputArea";
import { cqAPI } from "../../lib/Auth";
import "./CommentInput.css";

// Render a comment body as plain text with any http(s) URLs turned into
// clickable links. split() with a capturing group keeps the URLs as their own
// array entries, which we then test individually (the test regex has no /g flag
// so it isn't stateful).
const URL_SPLIT = /(https?:\/\/[^\s]+)/g;
const isUrl = (part) => /^https?:\/\//.test(part);
const renderBody = (body) =>
  String(body || "")
    .split(URL_SPLIT)
    .map((part, i) =>
      isUrl(part) ? (
        <Link
          key={i}
          href={part}
          isExternal
          color="blue.500"
          wordBreak="break-all"
        >
          {part}
        </Link>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );

// Comment thread + composer for a single PMSStatus record. Lists existing
// comments above a composer that reuses the ChatComponent <InputArea>. InputArea
// is a controlled-ish input: it mirrors its text into `questionText`, calls
// `onSend` on submit (then clears), and swaps the send button for a stop button
// while `isStreaming` — we hold the text locally, disable send while empty, and
// never stream.
function CommentInput({ statusId, comments = [], onCommentsChange }) {
  const [questionText, setQuestionText] = useState("");
  const toast = useToast();
  const theme = useTheme();
  const { colorMode } = useColorMode();

  const secondaryTextColor =
    colorMode === "light" ? theme.colors.gray[600] : theme.colors.gray[400];
  const cardBg =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  const handleSend = async (text) => {
    const comment = (text || "").trim();
    if (!comment) {
      return;
    }
    try {
      const response = await cqAPI.post("/monitoring/pms-status-comment", {
        statusId,
        comment,
      });
      // The post echoes back the full comment list for this status; replace the
      // displayed comments in place. A later refresh re-fetches everything.
      if (response?.data?.comments && onCommentsChange) {
        onCommentsChange(response.data.comments);
      }
    } catch (error) {
      console.error("Error posting PMS status comment:", error);
      toast({
        title: "Could not post comment",
        description: "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Box mt={4}>
      {comments.length > 0 && (
        <>
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            Comments
          </Text>
          <VStack align="stretch" spacing={3} mb={4}>
          {comments.map((comment, index) => (
            <Box
              key={comment._id || index}
              bg={cardBg}
              borderRadius="lg"
              p={3}
            >
              <Flex
                justify="space-between"
                align="baseline"
                wrap="wrap"
                rowGap={1}
                mb={1}
              >
                <Text fontSize="sm" fontWeight="bold">
                  {comment.email || "Unknown user"}
                </Text>
                {comment.created && (
                  <Text fontSize="xs" color={secondaryTextColor}>
                    {moment(comment.created).format("LLL")}
                  </Text>
                )}
              </Flex>
              <Text fontSize="sm" whiteSpace="pre-wrap">
                {renderBody(comment.body)}
              </Text>
            </Box>
          ))}
          </VStack>
        </>
      )}

      <Box className="pms-comment-input">
        <Text fontSize="sm" fontWeight="bold" mb={2}>
          Add a comment
        </Text>
        <InputArea
          onSend={handleSend}
          onStop={() => {}}
          questionText={questionText}
          setQuestionText={setQuestionText}
          disableSendButton={!questionText.trim()}
          isStreaming={false}
          placeholder="Add a comment about this PMS health status..."
        />
      </Box>
    </Box>
  );
}

export default CommentInput;
